/**
 * A quién se le puede reasignar una interacción, según quién lo pide.
 *
 * La regla la pone el negocio y **no es simétrica**: un administrador reparte
 * entre coordinadores (reparto de carga entre equipos), y un coordinador
 * reparte entre los suyos (reparto dentro del equipo). Un comercial no reparte
 * nada, así que ni siquiera ve la casilla de selección.
 *
 * | Quién mira          | Elige entre                | Y el destino real es      |
 * |---------------------|----------------------------|---------------------------|
 * | ADMINISTRADOR CRM   | Los grupos                 | El coordinador del grupo  |
 * | COORDINADOR CRM     | Los asesores a su cargo    | Esa persona               |
 * | El resto            | Nada (no ve la selección)  | —                         |
 *
 * El administrador elige un EQUIPO y no una persona, que es como se reparte la
 * carga de verdad; por debajo sigue siendo un id de usuario, así que el cuerpo
 * de la petición es el mismo en los dos casos (ver `fetchGroupTargets`).
 *
 * La jerarquía es REAL y sale de `helpers/teamOrg.js`, que es su fuente única
 * —la comparte con el panel «Gestión de mi equipo»—. Aquí solo se decide qué
 * trozo de ese organigrama aplica a quien mira.
 */
import { apiClient } from '@/api/softphone_api';
import {
  ADMIN_GROUP,
  COORDINATOR_GROUP,
  cargoDe,
  coordinadorDe,
  equipoDe,
  fetchOrgChart,
  nombreDe,
} from '@/modules/fxCrm/helpers/teamOrg';

export { ADMIN_GROUP, COORDINATOR_GROUP };

/** Es la plantilla de grupos del CRM, no un listado de gestión: cabe de sobra. */
const GROUPS_LIMIT = 200;

/**
 * El endpoint de asignación masiva. **Uno solo para los dos caminos**: lo que
 * cambia es el cuerpo, y es el cuerpo el que decide quién reparte (ver
 * `buildGroupAssignPayload` y `buildBulkAssignPayload`).
 */
const BULK_ASSIGN_URL = 'api/fxCrm/bulk_assign/';

/**
 * Qué alcance de reasignación tiene quien mira.
 * @returns {'admin'|'coordinator'|null}
 */
export function reassignScope(userInfo) {
  const groups = userInfo?.groups || [];
  if (groups.includes(ADMIN_GROUP)) return 'admin';
  if (groups.includes(COORDINATOR_GROUP)) return 'coordinator';
  return null;
}

/** ¿Se le ofrece la selección múltiple y el botón de reasignar? */
export const canReassign = (userInfo) => reassignScope(userInfo) !== null;

/**
 * La lista de destinos para quien está mirando.
 *
 * Devuelve `[]` para quien no puede reasignar en vez de lanzar: el modal ni
 * siquiera se le ofrece, y una lista vacía es más fácil de manejar que una
 * excepción en un camino que no debería ocurrir.
 *
 * Para el coordinador la fuente es **`CrmGroup/`, la misma que ve el
 * administrador**: así las dos listas no pueden contradecirse. El organigrama
 * derivado de `FxProfile` (`coordinador_asignado`) queda solo de respaldo, para
 * el caso de que esa consulta falle o no ubique su grupo.
 *
 * Backend va a exponer un endpoint propio para que un coordinador liste los
 * miembros de su grupo; cuando exista, sustituye a `fetchCoordinatorTeam` y el
 * respaldo se puede quitar.
 *
 * ⚠️ Al coordinador se le busca por `userInfo.User_id`, que es el id con el que
 * el resto del CRM identifica al usuario (`user_pbx`). **Queda por confirmar en
 * vivo que case con el `id` de los perfiles**; si no, esta búsqueda y el `user`
 * del payload de `single` hay que mapearlos. Es lo primero a revisar si la
 * lista sale vacía.
 *
 * ⚠️ `scope` se puede forzar. Por defecto sale del rol, pero quien ya decidió
 * qué pinta —la barra, el modal— tiene que poder imponerlo: si la pantalla
 * ofrece repartir en equipo y esta función responde con la lista de
 * coordinadores porque el rol dice otra cosa, se ve una lista que no
 * corresponde. Se nota con un usuario que tenga los dos roles.
 *
 * @param {object} userInfo
 * @param {'admin'|'coordinator'} [scope] Fuerza el alcance en vez de deducirlo.
 * @returns {Promise<Array<{id: number, name: string, role: string}>>}
 */
export async function fetchReassignTargets(userInfo, scope = reassignScope(userInfo)) {
  if (!scope) return [];

  if (scope === 'admin') {
    const org = await fetchOrgChart();
    return [...org.coordinators]
      .map(({ id, name, role }) => ({ id, name, role }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }

  // Primero su grupo, que es la misma verdad que ve el administrador.
  try {
    const equipo = await fetchCoordinatorTeam(userInfo);
    if (equipo) return equipo;
  } catch (error) {
    // Si no puede leer `CrmGroup/` —es pantalla de administrador y está sin
    // confirmar que un coordinador tenga permiso— se sigue por el respaldo en
    // vez de dejarlo sin lista.
    console.warn('No se pudo leer el grupo del coordinador:', error);
  }

  // Respaldo: el organigrama derivado de `coordinador_asignado`. Solo su gente
  // —el coordinador reparte, no se reparte a sí mismo—, igual que arriba.
  const org = await fetchOrgChart();
  const propio = coordinadorDe(org, userInfo?.User_id);
  return propio ? propio.members : [];
}

/**
 * Los miembros del grupo que coordina quien mira, desde `CrmGroup/`.
 *
 * **Sin el coordinador**: `members_detail` no lo incluye —Grupos lo guarda en
 * `coordinator_detail`, aparte— y así debe quedarse: reparte entre su gente.
 *
 * Devuelve `null` —y no `[]`— cuando no encuentra su grupo, para que quien
 * llama pueda distinguir «no tiene grupo aquí, prueba el respaldo» de «su grupo
 * está vacío», que son cosas distintas.
 */
async function fetchCoordinatorTeam(userInfo) {
  const miId = userInfo?.User_id;
  if (!miId) return null;

  const grupos = await fetchGroups();
  const propio = grupos.find(
    (grupo) => String(grupo?.coordinator_detail?.id) === String(miId)
  );
  if (!propio) return null;

  return miembrosDe(propio);
}

/** La lista cruda de grupos. Un solo sitio que arme la URL y el desempaque.
 *  Exportada para la Bandeja (`inboxTargets.js`). */
export async function fetchGroups() {
  const { data } = await apiClient.get(
    `api/fxCrm/CrmGroup/?limit=${GROUPS_LIMIT}`
  );
  return data?.results ?? data ?? [];
}

/** Los miembros de un grupo, sin su coordinador, ordenados por nombre. */
export function miembrosDe(grupo) {
  return (grupo?.members_detail || [])
    .map((miembro) => ({
      id: miembro.id,
      name: nombreDe(miembro),
      role: cargoDe(miembro),
    }))
    .sort((a, b) => a.name.localeCompare(b.name));
}

/**
 * Las opciones del filtro «Gestor»: la gente agrupada por equipo.
 *
 * ⚠️ **No es lo mismo que `fetchReassignTargets`**, aunque lo parezca. Aquel
 * responde «¿a quién puedo asignarle?» —para un administrador, coordinadores—;
 * este responde «¿de quién puedo ver cartera?», que para un administrador es
 * TODA la gente, repartida por grupos. Se intentó reutilizar uno para lo otro y
 * el filtro acababa ofreciendo solo coordinadores.
 *
 * **Quién aparece depende del alcance**, y no es simétrico:
 *
 *   · admin       → cada grupo con su coordinador primero y luego su gente.
 *                   El coordinador tiene que estar: cuando se asigna a un
 *                   equipo las interacciones quedan a su nombre, así que sin
 *                   él no habría forma de encontrarlas.
 *   · coordinador → solo sus asesores. Filtra la cartera de su gente; la suya
 *                   propia no es lo que viene a revisar aquí.
 *
 * `scope` se puede forzar por lo mismo que en `fetchReassignTargets`: quien
 * pinta la pantalla ya decidió qué camino sigue, y si aquí se volviera a
 * preguntar el rol podrían discrepar.
 *
 * @param {object} userInfo
 * @param {'admin'|'coordinator'} [scope] Fuerza el alcance en vez de deducirlo.
 * @returns {Promise<Array<{groupId, name, people: Array<{id, name, role}>}>>}
 */
export async function fetchManagerOptions(userInfo, scope = reassignScope(userInfo)) {
  if (!scope) return [];

  // No se deja explotar: `CrmGroup/` es pantalla de administrador y está sin
  // confirmar que un COORDINADOR CRM pueda leerla. Si responde 403, se sigue
  // por el respaldo de abajo en vez de dejar el filtro en «Todos» y ya.
  let grupos = [];
  try {
    grupos = await fetchGroups();
  } catch (error) {
    console.warn('No se pudo leer `CrmGroup/` para el filtro «Gestor»:', error);
  }

  const suyos =
    scope === 'admin'
      ? grupos
      : grupos.filter(
          (grupo) =>
            String(grupo?.coordinator_detail?.id) ===
            String(userInfo?.User_id)
        );

  const opciones = suyos
    .map((grupo) => {
      // El coordinador entra SIEMPRE, también en su propia lista: este filtro
      // responde «¿de quién puedo ver cartera?», y un coordinador gestiona la
      // suya —es lo mismo que dice `is_coordinator` en «Gestión de mi equipo»,
      // donde el jefe cuenta dentro de su equipo—. Dejarlo fuera hacía que sus
      // interacciones fueran las únicas no filtrables.
      const coordinador = grupo.coordinator_detail
        ? [
            {
              id: grupo.coordinator_detail.id,
              name: nombreDe(grupo.coordinator_detail),
              role: 'Coordinador comercial',
            },
          ]
        : [];
      return {
        groupId: grupo.id,
        name: grupo.name,
        people: [...coordinador, ...miembrosDe(grupo)],
      };
    })
    .filter((grupo) => grupo.people.length)
    .sort((a, b) => a.name.localeCompare(b.name));

  if (opciones.length || scope === 'admin') return opciones;

  /*
   * Respaldo del coordinador, el MISMO que ya tenía `fetchReassignTargets`.
   *
   * Que esta función no lo tuviera era la causa de que a un COORDINADOR CRM el
   * filtro «Gestor» le saliera con «Todos» y nada más: si no puede leer
   * `CrmGroup/` —o si su `coordinator_detail.id` no casa con `User_id`—, la
   * lista quedaba vacía y no había plan B. El modal de reasignar, que resuelve
   * lo mismo, sí lo tenía. Las dos ramas dan ahora la misma gente.
   */
  const org = await fetchOrgChart();
  const propio = coordinadorDe(org, userInfo?.User_id);
  if (!propio) return [];
  return [
    {
      groupId: null,
      name: propio.name ? `Equipo de ${propio.name}` : 'Mi equipo',
      people: equipoDe(propio),
    },
  ];
}

/**
 * Los grupos a los que un administrador puede pasar interacciones.
 *
 * ⚠️ **Lo que viaja es el `groupId`** (`buildGroupAssignPayload`), no el
 * coordinador. El `coordinatorId` y su nombre se conservan solo para la
 * PRESENTACIÓN —la línea «X · N personas» de cada opción y el aviso de «las
 * reparte X» al confirmar—: quién recibe cada interacción dentro del equipo lo
 * decide el servidor.
 *
 * Fuente: `fxCrm/CrmGroup/`, el mismo de Configuraciones → Grupos. No se deriva
 * de `FxProfile` como el organigrama porque aquí hace falta el NOMBRE del
 * equipo, que solo existe en este endpoint.
 *
 * Un grupo sin coordinador no se ofrece: no habría a quién asignarle. Puede
 * pasar si el backend lo permite, así que se filtra en vez de confiarse.
 *
 * ⚠️ Pendiente de confirmar que un COORDINADOR CRM pueda leer este endpoint —la
 * pantalla que lo usa hoy es solo de administrador—. Mientras tanto solo lo
 * llama el camino del admin.
 *
 * @returns {Promise<Array<{groupId, name, coordinatorId, coordinatorName, peopleCount}>>}
 */
export async function fetchGroupTargets() {
  const grupos = await fetchGroups();

  return grupos
    .filter((grupo) => grupo?.coordinator_detail?.id)
    .map((grupo) => ({
      groupId: grupo.id,
      name: grupo.name,
      coordinatorId: grupo.coordinator_detail.id,
      coordinatorName: nombreDe(grupo.coordinator_detail),
      // `people_count` ya incluye al coordinador; el respaldo lo suma a mano
      // para no contar de menos si ese campo faltara.
      peopleCount: grupo.people_count ?? (grupo.members_count ?? 0) + 1,
    }))
    .sort((a, b) => a.name.localeCompare(b.name));
}

/**
 * Cómo puede repartir un coordinador lo que tiene marcado.
 *
 * Vive aquí y no en la vista por lo mismo que el catálogo de acciones: el id es
 * lo único que entiende el backend, y el título y el detalle son presentación
 * de una decisión de negocio. Agregar un modo son dos cosas: una entrada aquí y
 * su rama en quien reparta.
 *
 * ⚠️ **Los `id` son los del backend, no rótulos internos**: viajan tal cual en
 * `mode`. Por eso están en inglés y no se traducen aquí —lo que se lee en
 * pantalla es `titulo`—.
 *
 * `single` es distinto a los otros tres y por eso va al final: no reparte nada,
 * abre el modal de siempre para elegir a alguien. Los otros tres los resuelve
 * el servidor.
 */
export const REPARTO_MODES = [
  {
    id: 'order',
    titulo: 'En orden',
    detalle: 'Recorre la lista del equipo de arriba abajo y vuelve a empezar.',
  },
  {
    id: 'random',
    titulo: 'Aleatoriamente',
    detalle: 'Sortea a quién le toca cada interacción.',
  },
  {
    id: 'least_load',
    titulo: 'Por menor carga',
    detalle:
      'Cada una va a quien tenga menos interacciones activas en ese momento.',
  },
  {
    id: 'single',
    titulo: 'A una persona',
    detalle: 'Todas las seleccionadas quedan con el mismo responsable.',
  },
];

/** El modo que NO reparte: abre el modal de elegir responsable. */
export const REPARTO_SINGLE = 'single';

/**
 * El cuerpo del **camino del coordinador**: reparte entre su gente.
 *
 * ```jsonc
 * { "mode": "order"|"random"|"least_load"|"single", "itx_managements": [90, 91] }
 * ```
 *
 * ⚠️ **En los tres modos automáticos se manda el CRITERIO, no el reparto ya
 * resuelto.** El front no calcula a quién le toca cada una, por tres motivos:
 *
 *   1. «Por menor carga» necesita cuántas activas tiene cada persona AHORA, un
 *      dato que esta vista no pide ni tiene por qué pedir.
 *   2. Dos coordinadores repartiendo a la vez calcularían sobre fotos viejas y
 *      dejarían la carga desbalanceada justo en lo que el modo promete evitar.
 *   3. El servidor tiene que validar el reparto igual, así que resolverlo dos
 *      veces solo abre la puerta a que no coincidan.
 *
 * ⚠️ **`single` es el único modo con destinatario, y el campo se llama `user`:**
 *
 * ```jsonc
 * { "mode": "single", "itx_managements": [90, 91], "user": 121 }
 * ```
 *
 * **No `user_pbx`.** Así se llama en el cambio de responsable de una sola
 * (`PATCH InteractionManagement/{id}/`), y es lo que este helper mandaba
 * mientras el contrato estaba por confirmar. El valor sí es el mismo id.
 *
 * `itx_managements` son ids de **gestión** (`ItxSimpleManagement.id`), no de
 * interacción: es lo que identifica la fila en esta vista.
 */
export function buildBulkAssignPayload(interactionIds, mode, userId = null) {
  const payload = {
    mode,
    itx_managements: [...interactionIds],
  };
  if (mode === REPARTO_SINGLE) payload.user = userId;
  return payload;
}

/**
 * El cuerpo del **camino del administrador**: pasa la carga a un equipo.
 *
 * ```jsonc
 * { "crm_group": 7, "itx_managements": [90, 91, 92] }
 * ```
 *
 * ⚠️ **Viaja el GRUPO, no el coordinador.** Es lo contrario de lo que se había
 * planteado: el front mandaba el `user_pbx` del coordinador porque el destino
 * real era una persona. Con este contrato el grupo es el destinatario y quién
 * lo recibe lo resuelve el servidor —que además puede repartir dentro del
 * equipo sin que el front se entere—.
 *
 * Por eso ya no hace falta `coordinatorId` para armar el cuerpo; se conserva en
 * `fetchGroupTargets` solo para poder decir en el aviso quién lo reparte.
 *
 * No lleva `mode`: la ausencia de `mode` y la presencia de `crm_group` es lo
 * que distingue este camino del del coordinador.
 */
export function buildGroupAssignPayload(interactionIds, crmGroupId) {
  return {
    crm_group: crmGroupId,
    itx_managements: [...interactionIds],
  };
}

/**
 * Manda la asignación. Un solo sitio que conozca la URL, igual que
 * `fetchGroups` es el único que arma la de los grupos.
 *
 * No captura el error a propósito: quien llama tiene que poder dejar el modal
 * abierto y avisar, y aquí no hay forma de saber cuál de los dos caminos fue.
 */
export function postBulkAssign(payload) {
  return apiClient.post(BULK_ASSIGN_URL, payload);
}
